import { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { Home, MessageCircle, User, Bell } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createTranslator } from '../../utils/detectLanguage';
import { homepagePtBR } from '../mappings/homepage.pt-br';
import { homepageES } from '../mappings/homepage.es';

const BottomNavBar = memo(({ user = null, showAuthPrompt }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);

  const t = useMemo(() => createTranslator({
    'pt-br': homepagePtBR,
    'es': homepageES
  }), []);

  // Listen for chat notifications from localStorage events
  useEffect(() => {
    // Check initial value
    const stored = localStorage.getItem('chatNotifications');
    if (stored) {
      setNotificationCount(parseInt(stored, 10) || 0);
    }

    // Listen for storage events (from other components)
    const handleStorageChange = (e) => {
      if (e.key === 'chatNotifications') {
        setNotificationCount(parseInt(e.newValue, 10) || 0);
      }
    };

    // Listen for custom events (from same tab)
    const handleNotification = (e) => {
      setNotificationCount(e.detail.count);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('chatNotification', handleNotification);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('chatNotification', handleNotification);
    };
  }, []);

  // Clear notifications when navigating to chat
  const handleChatClick = useCallback(() => {
    if (!user) {
      showAuthPrompt && showAuthPrompt('Start chatting with AI', { isTitle: true });
      return;
    }
    navigate('/chat'); // Navigate to chat page
    setNotificationCount(0);
    localStorage.setItem('chatNotifications', '0');
  }, [navigate, user, showAuthPrompt]);
  
  const tabs = useMemo(() => [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      path: '/home'
    },
    {
      id: 'chat',
      label: 'Chat',
      icon: MessageCircle,
      path: '/chat'
    },
    {
      id: 'account',
      label: 'Profile',
      icon: User,
      path: '/settings'
    }
  ], []);
  
  const isActive = useCallback((path) => {
    return location.pathname === path;
  }, [location.pathname]);
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/[0.08] z-50 safe-area-bottom">
      <div className="relative flex items-center justify-around px-3 py-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);

          // Special rendering for Chat tab with glassmorphic terminal vibe
          if (tab.id === 'chat') {
            return (
              <button
                key={tab.id}
                onClick={handleChatClick}
                className={`flex flex-col items-center justify-center min-w-[52px] p-1.5 rounded-lg transition-all duration-300 relative ${
                  active ? 'bg-white/[0.03]' : 'active:scale-95'
                }`}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
              >
                {/* Premium thin indicator - top position */}
                {active && (
                  <motion.div
                    layoutId="bottomNavIndicator"
                    className="absolute top-0 left-0 right-0 mx-auto w-8 h-[2px] bg-gradient-to-r from-violet-500/80 to-violet-600/80 rounded-b-full"
                    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                  />
                )}

                {/* Compact icon with subtle response */}
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  className={`transition-all duration-300 ${
                    active ? 'text-violet-400' : 'text-white/60'
                  }`}
                >
                  <div className="relative">
                    <Icon className="w-[18px] h-[18px]" />

                    {/* Terminal-style notification dot */}
                    <AnimatePresence>
                      {notificationCount > 0 && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          className="absolute -top-1 -right-1"
                        >
                          <div className="relative">
                            <motion.div
                              animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.5, 0.2, 0.5]
                              }}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                              className="absolute inset-0 bg-white rounded-full blur"
                            />
                            <div className="relative w-2 h-2 bg-white/90 rounded-full" />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>

                {/* Minimal label - always show for Chat */}
                <motion.span
                  className="text-[9px] font-medium tracking-wide text-white/90 mt-0.5"
                >
                  {t('Chat')}
                </motion.span>
              </button>
            );
          }

          // Regular tabs - premium compact style
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex flex-col items-center justify-center min-w-[52px] p-1.5 rounded-lg transition-all duration-300 relative ${
                active ? 'bg-white/[0.03]' : 'active:scale-95'
              }`}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
            >
              {/* Compact icon with subtle response */}
              <motion.div
                whileTap={{ scale: 0.9 }}
                className={`transition-all duration-300 ${
                  active ? 'text-emerald-400' : 'text-white/60'
                }`}
              >
                <Icon className="w-[18px] h-[18px]" />
              </motion.div>

              {/* Minimal label - always show */}
              <motion.span
                className={`text-[9px] font-medium tracking-wide mt-0.5 ${
                  active ? 'text-white/90' : 'text-white/60'
                }`}
              >
                {t(tab.label).split(' ')[0]}
              </motion.span>

              {/* Premium thin indicator - top position */}
              {active && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute top-0 left-0 right-0 mx-auto w-8 h-[2px] bg-gradient-to-r from-emerald-500/80 to-violet-500/80 rounded-b-full"
                  transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Subtle bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.02] to-transparent" />
    </div>
  );
});

BottomNavBar.displayName = 'BottomNavBar';

export default BottomNavBar;